import { AbstractClient, CompositionClient } from "@/app/client";
import * as types from "@/app/types";
import { UUID } from "crypto";

export default class APITags extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  /**
   * List all tags for the current company, ordered by priority
   */
  list = async (): Promise<types.TagResponse[]> => {
    const res = await this._fetch({
      url: `/tags/`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Get a specific tag by ID
   */
  get = async (tagId: UUID): Promise<types.TagResponse> => {
    const res = await this._fetch({
      url: `/tags/${tagId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Create a new tag
   */
  create = async (body: types.TagCreateRequest): Promise<types.TagResponse> => {
    const res = await this._fetch({
      url: `/tags/`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Update an existing tag
   */
  update = async (
    tagId: UUID,
    body: types.TagUpdateRequest,
  ): Promise<types.TagResponse> => {
    const res = await this._fetch({
      url: `/tags/${tagId}`,
      method: "PUT",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Delete a tag
   */
  delete = async (tagId: UUID): Promise<void> => {
    await this._fetch({
      url: `/tags/${tagId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
  };

  /**
   * Reorder tags by providing the new order of tag IDs
   */
  reorder = async (
    body: types.TagReorderRequest,
  ): Promise<types.TagResponse[]> => {
    const res = await this._fetch({
      url: `/tags/reorder`,
      method: "PUT",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Batch add tags to multiple products
   */
  batchAddTags = async (
    body: types.BatchTagsRequest,
  ): Promise<types.BatchTagsResponse> => {
    const res = await this._fetch({
      url: `/tags/batch/add`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Batch remove tags from multiple products
   */
  batchRemoveTags = async (
    body: types.BatchTagsRequest,
  ): Promise<types.BatchTagsResponse> => {
    const res = await this._fetch({
      url: `/tags/batch/remove`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Batch replace all tags on multiple products
   */
  batchReplaceTags = async (
    body: types.BatchTagsRequest,
  ): Promise<types.BatchTagsResponse> => {
    const res = await this._fetch({
      url: `/tags/batch/replace`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Get all tags for a specific product
   */
  getProductTags = async (productId: UUID): Promise<types.TagResponse[]> => {
    const res = await this._fetch({
      url: `/tags/products/${productId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  /**
   * Sync (replace) all tags for a specific product
   */
  syncProductTags = async (
    productId: UUID,
    body: types.ProductTagsSyncRequest,
  ): Promise<types.TagResponse[]> => {
    const res = await this._fetch({
      url: `/tags/products/${productId}`,
      method: "PUT",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };
}
